import { useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  ShieldCheck,
  Users,
  X,
  Check,
  Ban,
  Search,
  FileText,
  Loader2,
} from "lucide-react";
import CompanyAdminLayout from "../../layouts/CompanyAdminLayout";
import {
  approveScenario,
  getPendingApprovals,
  rejectScenario,
} from "../../api/services/companyAdminService";

export default function CompanyAdminApprovals() {
  const [requests, setRequests] = useState([]);
  const [activeRequestId, setActiveRequestId] = useState(null);
  const [decisionType, setDecisionType] = useState(null);
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("All Teams");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeRequest = useMemo(
    () => requests.find((r) => r.id === activeRequestId) || null,
    [requests, activeRequestId],
  );

  const teams = useMemo(() => {
    const uniqueTeams = new Set();
    requests.forEach((r) => {
      if (r.team) uniqueTeams.add(r.team);
    });
    return ["All Teams", ...Array.from(uniqueTeams).sort()];
  }, [requests]);

  const filteredRequests = requests.filter((r) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      r.boxName.toLowerCase().includes(q) ||
      r.requester.toLowerCase().includes(q) ||
      r.team.toLowerCase().includes(q);
    return (
      matchesSearch &&
      (teamFilter === "All Teams" || r.team === teamFilter) &&
      r.status === "pending"
    );
  });

  // ── Load approvals ──
  useEffect(() => {
    let isMounted = true;

    const formatDateTime = (value) => {
      if (!value) return "";
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
    };

    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const data = await getPendingApprovals();
        if (!isMounted) return;
        setRequests(
          Array.isArray(data)
            ? data.map((item) => ({
                id: item.id,
                boxName: item.title || "Untitled scenario",
                category: item.category_name || "Uncategorized",
                difficulty: item.difficulty || "Unknown",
                team: item.team_name || "Unassigned",
                requester: item.requester_name || "Unknown requester",
                requesterRole: item.designation || "Requester",
                createdAt: formatDateTime(item.created_at),
                summary: item.description || "No summary provided.",
                notes: item.description || "No additional notes provided.",
                status: "pending",
              }))
            : [],
        );
      } catch (err) {
        if (isMounted)
          setLoadError(err?.message || "Failed to load approvals.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, []);

  // ── Handlers ──
  const openDecisionModal = (requestId, type) => {
    setActiveRequestId(requestId);
    setDecisionType(type);
    setSubmitError(null);
  };

  const closeDecisionModal = () => {
    if (isSubmitting) return;
    setActiveRequestId(null);
    setDecisionType(null);
    setSubmitError(null);
  };

  // Collapsed — approve and reject follow identical flow, only API call differs
  const handleDecision = async () => {
    if (!activeRequest || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (decisionType === "approved") {
        await approveScenario(activeRequest.id);
      } else {
        await rejectScenario(activeRequest.id);
      }
      setRequests((prev) => prev.filter((r) => r.id !== activeRequest.id));
      closeDecisionModal();
    } catch (err) {
      setSubmitError(
        err?.message ||
          `Failed to ${decisionType === "approved" ? "approve" : "reject"} scenario.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTeamFilter = (team) => {
    setTeamFilter(team);
    setSearch("");
  };

  // ── Render ──
  return (
    <CompanyAdminLayout>
      <div className="min-h-screen p-6 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold text-(--color-text-primary)">
              Approvals
            </h1>
            <p className="text-xs mt-0.5 text-(--color-text-muted)">
              Review requests to publish hackboxes company-wide.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-(--color-border) text-xs text-(--color-text-muted) bg-(--color-bg-card)">
            <ShieldCheck className="w-4 h-4 text-(--color-primary)" />
            {filteredRequests.length} pending
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-text-muted)" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by box, requester, or team..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-(--color-border) bg-(--color-bg)/50 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:outline-none focus:border-(--color-primary) focus:shadow-[0_0_0_3px_var(--color-primary-100)] transition-all"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {teams.map((team) => (
              <button
                key={team}
                onClick={() => handleTeamFilter(team)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  teamFilter === team
                    ? "bg-(--color-primary) text-(--color-text-on-primary)"
                    : "bg-(--color-bg)/50 border border-(--color-border) text-(--color-text-muted) hover:text-(--color-text-primary)"
                }`}
              >
                {team}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4">
          {/* Load error */}
          {!isLoading && loadError && (
            <div className="rounded-lg border border-dashed border-red-500/40 bg-red-500/5 p-6 text-sm text-red-400">
              {loadError}
            </div>
          )}

          {/* Loading skeleton */}
          {isLoading && (
            <div className="flex flex-col gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-lg border border-(--color-border) bg-(--color-bg-card) p-5 h-40 animate-pulse"
                />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !loadError && filteredRequests.length === 0 && (
            <div className="flex flex-col items-center justify-center min-h-105 text-center px-4">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-2xl bg-(--color-primary)/10 blur-xl scale-150 opacity-60" />
                <div className="relative w-20 h-20 rounded-2xl bg-(--color-primary)/10 border border-(--color-primary)/20 flex items-center justify-center shadow-sm">
                  <ShieldCheck className="w-9 h-9 text-(--color-primary)" />
                </div>
                <div className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-emerald-500 border-2 border-(--color-bg) flex items-center justify-center shadow">
                  <Check className="w-3 h-3 text-white" />
                </div>
              </div>
              <h3 className="text-base font-semibold text-(--color-text-primary) mb-2">
                All caught up!
              </h3>
              <p className="text-xs text-(--color-text-primary) max-w-xs leading-relaxed">
                No pending approvals right now. New hackbox publish requests
                will appear here for your review.
              </p>
              <div className="flex items-center gap-3 mt-6 w-full max-w-xs">
                <div className="flex-1 h-px bg-(--color-border)" />
                <span className="text-[11px] text-(--color-text-muted) whitespace-nowrap">
                  Nothing to action
                </span>
                <div className="flex-1 h-px bg-(--color-border)" />
              </div>
            </div>
          )}

          {/* Request cards */}
          {!isLoading &&
            !loadError &&
            filteredRequests.map((request) => (
              <div
                key={request.id}
                className="relative rounded-md border border-(--color-border) bg-(--color-bg)/30 p-5 pl-6"
              >
                <div className="absolute left-0 top-4 bottom-4 w-1.5 rounded-full bg-(--color-primary)" />
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <h2 className="text-lg font-semibold text-(--color-text-primary)">
                      {request.boxName}
                    </h2>
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 text-emerald-400 text-xs font-medium">
                        Pending
                      </span>
                      <span className="text-[11px] text-(--color-text-muted)">
                        {request.createdAt}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-(--color-text-muted)">
                    {request.summary}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      {[
                        { icon: Users, label: "Team", value: request.team },
                        {
                          icon: BadgeCheck,
                          label: "Requester",
                          value: request.requester,
                        },
                        {
                          icon: FileText,
                          label: "Category",
                          value: request.category,
                        },
                        {
                          icon: ShieldCheck,
                          label: "Difficulty",
                          value: request.difficulty,
                        },
                      ].map(({ icon: Icon, label, value }) => (
                        <div
                          key={label}
                          className="rounded-lg border border-(--color-border) bg-(--color-bg)/50 p-3"
                        >
                          <div className="flex items-center gap-2 text-(--color-text-muted)">
                            <Icon className="w-3.5 h-3.5 text-(--color-primary)" />
                            {label}
                          </div>
                          <div className="text-(--color-text-primary) mt-1">
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() =>
                          openDecisionModal(request.id, "approved")
                        }
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-(--color-primary) text-white dark:text-black text-xs font-medium hover:opacity-90 transition-colors"
                      >
                        <Check className="w-3.5 h-3.5" />
                        Approve
                      </button>
                      <button
                        onClick={() =>
                          openDecisionModal(request.id, "rejected")
                        }
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red-500/40 text-red-400 text-xs font-medium hover:bg-red-500/10 transition-colors"
                      >
                        <Ban className="w-3.5 h-3.5" />
                        Reject
                      </button>
                    </div>
                  </div>

                  <div className="px-3 py-2 text-xs text-(--color-text-muted)">
                    <span className="text-(--color-text-primary) italic">
                      Note: Approving shares this scenario across the entire
                      organization. Rejecting keeps it limited to the current
                      team or requester scope.
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* ── Decision Modal ── */}
      {activeRequest && decisionType && (
        <div className="fixed inset-0 bg-(--color-bg-overlay) flex items-center justify-center z-50 p-4">
          <div className="bg-(--color-bg) rounded-md shadow-2xl w-full max-w-lg border border-(--color-border) overflow-hidden">
            <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-(--color-border)">
              <div>
                <h2 className="text-sm font-semibold text-(--color-text-primary)">
                  {decisionType === "approved" ? "Approve" : "Reject"} request
                </h2>
                <p className="text-xs text-(--color-text-muted) mt-1">
                  {activeRequest.boxName} · {activeRequest.team}
                </p>
              </div>
              <button
                onClick={closeDecisionModal}
                disabled={isSubmitting}
                className="w-7 h-7 flex items-center justify-center text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-3 text-xs text-(--color-text-muted)">
              <div className="rounded-lg border border-(--color-border) bg-(--color-bg-card) p-3">
                <div className="text-(--color-text-primary) font-medium">
                  {activeRequest.requester}
                </div>
                <div>{activeRequest.requesterRole}</div>
                <div className="mt-2 text-(--color-text-primary)">
                  {activeRequest.summary}
                </div>
              </div>
              <p>
                {decisionType === "approved"
                  ? "This will make the hackbox visible to the entire company. Please confirm your decision."
                  : "This will keep the hackbox limited to its current team. Please confirm your decision."}
              </p>
              {submitError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                  {submitError}
                </div>
              )}
            </div>

            <div className="px-6 py-3.5 border-t border-(--color-border) flex gap-2.5">
              <button
                type="button"
                onClick={closeDecisionModal}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 border border-(--color-border) text-(--color-text-primary) rounded-lg hover:bg-(--color-bg-overlay) transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDecision}
                disabled={isSubmitting}
                className={`flex-1 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed ${
                  decisionType === "approved"
                    ? "bg-(--color-primary) text-white dark:text-black hover:opacity-90"
                    : "bg-red-500/90 text-white hover:bg-red-500"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {decisionType === "approved"
                      ? "Approving..."
                      : "Rejecting..."}
                  </>
                ) : decisionType === "approved" ? (
                  "Approve"
                ) : (
                  "Reject"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </CompanyAdminLayout>
  );
}
